import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import type { AttendanceSummaryView, SupportedLocale } from '@stm/contracts';
import { translate } from '@stm/i18n';
import { ApiRequestError, apiRequest } from './api';
import { athleteDisplayName } from './athletes';
import { percentLabel } from './scheduling';
import { AttendanceHistory } from './AttendanceHistory';

export interface AttendanceSummaryProps {
  locale: SupportedLocale;
  /** `/api/v1/organizations/{id}` */
  base: string;
  fromDate: string;
  toDate: string;
  departmentId: string;
}

/** Attendance rates for the calendar's period, always shown with the definition they follow. */
export function AttendanceSummary({
  locale,
  base,
  fromDate,
  toDate,
  departmentId,
}: AttendanceSummaryProps): React.JSX.Element {
  const [summary, setSummary] = useState<AttendanceSummaryView | null>(null);
  const [errorKey, setErrorKey] = useState<ApiRequestError['messageKey'] | null>(null);

  async function load(): Promise<void> {
    setErrorKey(null);
    const parameters = new URLSearchParams({ from: fromDate, to: toDate });
    if (departmentId !== '') parameters.set('departmentId', departmentId);
    try {
      setSummary(
        await apiRequest<AttendanceSummaryView>(
          `${base}/attendance-summary?${parameters.toString()}`,
        ),
      );
    } catch (error) {
      setErrorKey(error instanceof ApiRequestError ? error.messageKey : 'error.unexpected');
    }
  }

  return (
    <Box
      component="section"
      aria-label={translate(locale, 'calendar.summary.title')}
      sx={{ mt: 4 }}
    >
      <Typography variant="h5" component="h2" gutterBottom>
        {translate(locale, 'calendar.summary.title')}
      </Typography>
      <Button
        variant="outlined"
        sx={{ minHeight: 44 }}
        onClick={() => {
          void load();
        }}
      >
        {translate(locale, 'calendar.summary.show')}
      </Button>
      {errorKey !== null && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {translate(locale, errorKey)}
        </Alert>
      )}
      {summary?.rows.length === 0 && (
        <Typography color="text.secondary" sx={{ mt: 1 }}>
          {translate(locale, 'calendar.summary.empty')}
        </Typography>
      )}
      {summary !== null && summary.rows.length > 0 && (
        <Box sx={{ overflowX: 'auto', mt: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{translate(locale, 'calendar.summary.athlete')}</TableCell>
                <TableCell align="right">
                  {translate(locale, 'calendar.attendance.present')}
                </TableCell>
                <TableCell align="right">{translate(locale, 'calendar.attendance.late')}</TableCell>
                <TableCell align="right">
                  {translate(locale, 'calendar.attendance.absent_excused')}
                </TableCell>
                <TableCell align="right">
                  {translate(locale, 'calendar.attendance.absent_unexcused')}
                </TableCell>
                <TableCell align="right">{translate(locale, 'calendar.summary.rate')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {summary.rows.map((row) => (
                <TableRow key={row.athleteId}>
                  <TableCell>{athleteDisplayName(row, locale)}</TableCell>
                  <TableCell align="right">{row.present}</TableCell>
                  <TableCell align="right">{row.late}</TableCell>
                  <TableCell align="right">{row.absentExcused}</TableCell>
                  <TableCell align="right">{row.absentUnexcused}</TableCell>
                  <TableCell align="right">{percentLabel(row.rate)}</TableCell>
                </TableRow>
              ))}
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>
                  {translate(locale, 'calendar.summary.total')}
                </TableCell>
                <TableCell align="right">{summary.totals.present}</TableCell>
                <TableCell align="right">{summary.totals.late}</TableCell>
                <TableCell align="right">{summary.totals.absentExcused}</TableCell>
                <TableCell align="right">{summary.totals.absentUnexcused}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600 }}>
                  {percentLabel(summary.totals.rate)}
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Box>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
        {translate(locale, 'calendar.summary.definition')}
      </Typography>
      <AttendanceHistory
        locale={locale}
        base={base}
        fromDate={fromDate}
        toDate={toDate}
        departmentId={departmentId}
      />
    </Box>
  );
}
